/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GetObjectCommand,
  PutObjectCommand,
  HeadObjectCommand,
  ListObjectsCommand,
  S3Client,
  type GetObjectCommandOutput,
  type ListObjectsCommandOutput,
  type PutObjectCommandInput,
  type PutObjectCommandOutput,
  type DeleteObjectCommandOutput,
  DeleteObjectCommand
} from '@aws-sdk/client-s3'
import { S3 } from 'aws-sdk'
import { getSignedUrl as GetSignedURL } from '@aws-sdk/s3-request-presigner'
import { useRouter } from 'vue-router'
import { setLastAccessDate, procAtSignout } from '@/features/miiaFw/utils/miiaFwCognito'
import type { TokenInfo } from '../types/cognito/auth'

import {
  webApiCall,
  // alert,
  stringToArrayBuffer,
  stringToBlob,
  assert
} from '@/features/miiaFw/utils/miiaFwUtils'

import { useToken } from './useToken'
import MiiaFwConfig from '@/configs/config'
import { useCommonModalStore } from '@/stores/commonModalStore'

interface MiiaS3ListObjectOutputs {
  contents: ListObjectsCommandOutput['Contents']
  commonPrefixes: ListObjectsCommandOutput['CommonPrefixes']
}

/** S3 バケットの情報を格納するオブジェクト */
interface BucketParameter {
  /** バケット名 */
  name: string
  /** 表示するオブジェクトのプレフィックス */
  prefix: string
  /** オブジェクトをグループ化するのに用いられている区切り文字 */
  delimiter: string
}

/** S3インスタンスを生成する時に入れる引数(base) */
interface S3ClientBaseConfig {
  /** S3のリージョン */
  region: string
  /** VPCエンドポイント(Interface) */
  endpoint?: string
}

const s3ClientBaseConfig: S3ClientBaseConfig = {
  region: MiiaFwConfig.config.region
}
if (typeof MiiaFwConfig.config.s3Access?.vpcEndpoint === 'string') {
  s3ClientBaseConfig.endpoint = MiiaFwConfig.config.s3Access.vpcEndpoint
}

export const useMiiaFwS3 = () => {
  const { procUnderAuthed, refreshToken } = useToken()
  /**
   * ファイル一覧の取得する。
   * @param bucketParam バケットを指定するパラメータ
   * @returns If resolved then {commonPrefixes: [{Prefix: 'bucketParamで指定したprefix直下のフォルダ'}, ...], contents: [{Key: 'オブジェクトの名前', LastModified: 最終更新日時, ETag: "ETag", Size: ファイルサイズ(Byte), StorageClass: "ストレージクラス"}, {}, ...]}.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function getFileList(bucketParam: BucketParameter): Promise<MiiaS3ListObjectOutputs> {
    return await new Promise<MiiaS3ListObjectOutputs>((resolve, reject) => {
      procUnderAuthed((tokens: TokenInfo) => {
        if (
          (MiiaFwConfig.config.s3Access.endpoint === undefined ||
            MiiaFwConfig.config.s3Access.endpoint === null) &&
          (MiiaFwConfig.config.authorization.authEndpoint === undefined ||
            MiiaFwConfig.config.authorization.authEndpoint === null)
        ) {
          // S3 Direct
          getFileListProc(bucketParam)
            .then((result: any) => {
              resolve(result)
            })
            .catch(error => {
              reject(error)
            })
        } else {
          // 閉域網
          getFileListProcClosed(tokens, bucketParam)
            .then((result: any) => {
              resolve(result)
            })
            .catch(error => {
              reject(error)
            })
        }
      }, false)
    })
  }

  /**
   * S3 Bucket からオブジェクトをダウンロードする。
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @param objType オブジェクトダウンロードする時の型。binary, base64, blob, objectUrlが指定可能。
   * @param timeout タイムアウトまでの時間(msec)
   */
  async function downloadObject(
    bucketParam: BucketParameter,
    fileName: string,
    objType: string,
    timeout: number = 60000
  ): Promise<any> {
    return await new Promise((resolve, reject) => {
      procUnderAuthed((tokens: TokenInfo) => {
        if (
          MiiaFwConfig.config.s3Access.endpoint == null &&
          MiiaFwConfig.config.authorization.authEndpoint == null
        ) {
          // S3 Direct
          downloadObjectProc(bucketParam, fileName, objType, timeout)
            .then((result: any) => {
              resolve(result)
            })
            .catch((error: any) => {
              reject(error)
            })
        } else {
          // 閉域網
          downloadObjectProcClosed(tokens, bucketParam, fileName, objType, timeout)
            .then(result => {
              resolve(result)
            })
            .catch(error => {
              reject(error)
            })
        }
      }, false)
    })
  }

  /**
   * Bucketにオブジェクトが存在するか確認する。
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @returns If resolved then True or False.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function exists(bucketParam: BucketParameter, fileName: string): Promise<boolean | any> {
    return await new Promise((resolve, reject) => {
      procUnderAuthed((tokens: TokenInfo) => {
        if (
          (MiiaFwConfig.config.s3Access.endpoint === undefined ||
            MiiaFwConfig.config.s3Access.endpoint === null) &&
          (MiiaFwConfig.config.authorization.authEndpoint === undefined ||
            MiiaFwConfig.config.authorization.authEndpoint === null)
        ) {
          // S3 Direct
          existsProc(bucketParam, fileName)
            .then(result => {
              resolve(result)
            })
            .catch(error => {
              reject(error)
            })
        } else {
          // 閉域網
          existsProcClosed(tokens, bucketParam, fileName)
            .then(result => {
              resolve(result)
            })
            .catch(error => {
              reject(error)
            })
        }
      }, false)
    })
  }

  /**
   * uploadDataが呼び出す、S3 Bucketへデータをアップロードする処理の本体(閉域網用)。
   * @param bucketParam バケットを指定するパラメータ
   * @param contentType コンテンツのフォーマット（Content-Type）
   * @param fileName ファイル名(S3上でのフルパス)
   * @param file オブジェクトデータ
   * @param timeout タイムアウトまでの時間(msec)
   * @returns If resolved then リクエストに対するレスポンス(アップロードされたオブジェクトについての情報）.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function uploadData(
    bucketParam: BucketParameter,
    contentType: string,
    fileName: string,
    file: PutObjectCommandInput['Body'],
    timeout: number = 60000
  ): Promise<any> {
    return await new Promise((resolve, reject) => {
      procUnderAuthed((tokens: TokenInfo) => {
        if (
          (MiiaFwConfig.config.s3Access.endpoint === undefined ||
            MiiaFwConfig.config.s3Access.endpoint === null) &&
          (MiiaFwConfig.config.authorization.authEndpoint === undefined ||
            MiiaFwConfig.config.authorization.authEndpoint === null)
        ) {
          // S3 Direct
          uploadDataProc(bucketParam, contentType, fileName, file, timeout)
            .then(result => {
              resolve(result)
            })
            .catch(error => {
              reject(error)
            })
        } else {
          // 閉域網
          if (file instanceof File) {
            uploadDataProcClosed(tokens, bucketParam, contentType, fileName, file)
              .then(result => {
                resolve(result)
              })
              .catch(error => {
                reject(error)
              })
          } else {
            throw new Error('閉域網では File 以外を upload することができません')
          }
        }
      }, false)
    })
  }
  /**
   * S3 Bucket内のファイルの削除処理を行う。
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @returns バージョン管理されていないBucketなら空のオブジェクト{}を返す。
   * バージョン管理されているBucketなら { DeleteMarker: (boolean)削除したオブジェクトにDeleteMarkerが付けられているかどうか,
   * VersionId: (String)削除によって作成されたDeleteMarkerのバージョン,
   * RequestCharged: (String)リクエストが正常に課金されているかどうか }
   * を返す。
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function deleteFile(bucketParam: BucketParameter, fileName: string): Promise<any> {
    return await new Promise((resolve, reject) => {
      procUnderAuthed((tokens: TokenInfo) => {
        if (
          (MiiaFwConfig.config.s3Access.endpoint === undefined ||
            MiiaFwConfig.config.s3Access.endpoint === null) &&
          (MiiaFwConfig.config.authorization.authEndpoint === undefined ||
            MiiaFwConfig.config.authorization.authEndpoint === null)
        ) {
          // S3 Direct
          deleteFileProc(bucketParam, fileName)
            .then(result => {
              resolve(result)
            })
            .catch(error => {
              reject(error)
            })
        } else {
          // 閉域網
          deleteFileProcClosed(tokens, bucketParam, fileName)
            .then(result => {
              resolve(result)
            })
            .catch(error => {
              reject(error)
            })
        }
      }, false)
    })
  }

  /**
   * getFileListが呼び出す、ファイル一覧の取得処理を行う本体。
   * @param bucketParam バケットを指定するパラメータ
   * @returns If resolved then {commonPrefixes: [{Prefix: 'bucketParamで指定したprefix直下のフォルダ'}, ...], contents: [{Key: 'オブジェクトの名前', LastModified: 最終更新日時, ETag: "ETag", Size: ファイルサイズ(Byte), StorageClass: "ストレージクラス"}, {}, ...]}.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function getFileListProc(bucketParam: BucketParameter): Promise<MiiaS3ListObjectOutputs> {
    const client = new S3Client({ ...s3ClientBaseConfig, maxAttempts: 100 })
    const params = {
      Bucket: bucketParam.name,
      Prefix: bucketParam.prefix,
      Delimiter: bucketParam.delimiter
    }
    const command = new ListObjectsCommand(params)
    return await new Promise((resolve, reject) => {
      // S3 SDKやpromiseはキャンセル不可能でタイムアウトもしないので、独自に実装する。
      const func = (): void => {
        reject({
          message: 'API実行に失敗しました。\n原因: アクセス回線障害など'
        })
      }
      const timer = setTimeout(func, 20000)
      // listObjects実行には、AWSポリシで「Action → s3:ListBucket、Resource → arn:aws:s3:::<Bucket名>」権限が必要
      client
        .send(command)
        .then(data => {
          clearTimeout(timer)
          setLastAccessDate(Date.now())
          resolve({
            commonPrefixes: data.CommonPrefixes,
            contents: data.Contents
          })
        })
        .catch(error => {
          clearTimeout(timer)
          if (error.code === 'CredentialsError') {
            // STSトークンの期限切れ
            refreshToken()
              .then(() => {
                getFileList(bucketParam)
                  .then(contents => {
                    resolve(contents)
                  })
                  .catch(retryError => {
                    reject({ message: retryError })
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
              })
          } else {
            setLastAccessDate(Date.now())
            reject({ message: error })
          }
        })
    })
  }

  /**
   * downloadObjectが呼び出す、S3 Bucketからオブジェクトをダウンロードする処理の本体。
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @param objType オブジェクトダウンロードする時の型。binary, base64, blob, objectUrlが指定可能。
   * @param timeout タイムアウトまでの時間(msec)
   * @returns If resolved then オブジェクトデータ.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function downloadObjectProc(
    bucketParam: BucketParameter,
    fileName: string,
    objType: string,
    timeout: number = 5000
  ): Promise<S3.GetObjectOutput['Body']> {
    const s3 = new S3({ ...s3ClientBaseConfig })
    const key = bucketParam.prefix + fileName
    const params = { Bucket: bucketParam.name, Key: key }
    return await new Promise((resolve, reject) => {
      // S3 SDKやpromiseはキャンセル不可能でタイムアウトもしないので、独自に実装する。
      const func = (): void => {
        reject({
          message: 'API実行に失敗しました。\n原因: アクセス回線障害など'
        })
      }
      const timer = setTimeout(func, timeout)
      s3.getObject(params, (error, result) => {
        clearTimeout(timer)
        if (error) {
          if (error.code === 'CredentialsError') {
            // STSトークンの期限切れ
            refreshToken()
              .then(() => {
                downloadObjectProc(bucketParam, fileName, objType, timeout)
                  .then(contents => {
                    resolve(contents)
                  })
                  .catch(retryError => {
                    reject({ message: retryError })
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
              })
          } else {
            setLastAccessDate(Date.now())
            reject({ message: error })
          }
        } else {
          setLastAccessDate(Date.now())
          if (objType.toLowerCase() === 'binary') {
            resolve(result.Body)
          }
          if (objType.toLowerCase() === 'base64') {
            resolve(window.btoa(String.fromCharCode.apply(String as any, result.Body as any)))
          }
          const blob = new Blob([result.Body as any], {
            type: result.ContentType
          })
          if (objType.toLowerCase() === 'blob') {
            resolve(blob)
          }
          if (objType.toLowerCase() === 'objecturl') {
            resolve(window.URL.createObjectURL(blob))
          } else {
            reject({
              message: 'ダウンロードするオブジェクトの型指定が不正です。'
            })
          }
        }
      })
    })
  }
  /**
   * existsProcが呼び出す、Bucketにオブジェクトが存在するか確認する処理の本体。
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @returns If resolved then True or False.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function existsProc(bucketParam: BucketParameter, fileName: string): Promise<boolean> {
    const client = new S3Client(s3ClientBaseConfig)
    const key = bucketParam.prefix + fileName
    const params = { Bucket: bucketParam.name, Key: key }
    const command = new HeadObjectCommand(params)
    return await new Promise((resolve, reject) => {
      // S3 SDKやpromiseはキャンセル不可能でタイムアウトもしないので、独自に実装する。
      const func = (): void => {
        reject({
          message: 'API実行に失敗しました。\n原因: アクセス回線障害など'
        })
      }
      const timer = setTimeout(func, 5000)
      client
        .send(command)
        .then(_result => {
          clearTimeout(timer)
          setLastAccessDate(Date.now())
          resolve(true)
        })
        .catch(error => {
          clearTimeout(timer)
          if (error.code === 'CredentialsError') {
            // STSトークンの期限切れ
            refreshToken()
              .then(_tokens => {
                existsProc(bucketParam, fileName)
                  .then(contents => {
                    resolve(contents)
                  })
                  .catch(retryError => {
                    reject({ message: retryError })
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
              })
          } else {
            setLastAccessDate(Date.now())
            const errorMsg = error.toString().toLowerCase()
            if (errorMsg.includes('notfound') && errorMsg.includes('null')) {
              resolve(false)
            } else {
              reject({ message: error.toString() })
            }
          }
        })
    })
  }
  /**
   * uploadDataが呼び出す、S3 Bucketへデータをアップロードする処理の本体。
   * @param bucketParam バケットを指定するパラメータ
   * @param contentType コンテンツのフォーマット（Content-Type）
   * @param fileName ファイル名(S3上でのフルパス)
   * @param contents オブジェクトデータ
   * @param timeout タイムアウトまでの時間(msec)
   * @returns If resolved then リクエストに対するレスポンス(アップロードされたオブジェクトについての情報）.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function uploadDataProc(
    bucketParam: BucketParameter,
    contentType: string,
    fileName: string,
    contents: PutObjectCommandInput['Body'],
    timeout: number = 5000
  ): Promise<any> {
    const client = new S3Client(s3ClientBaseConfig)
    const key = bucketParam.prefix + fileName
    const params: PutObjectCommandInput = {
      Bucket: bucketParam.name,
      Key: key,
      ContentType: contentType,
      Body: contents
    }
    const command = new PutObjectCommand(params)
    return await new Promise((resolve, reject) => {
      // S3 SDKやpromiseはキャンセル不可能でタイムアウトもしないので、独自に実装する。
      const func = (): void => {
        reject({
          message: 'API実行に失敗しました。\n原因: アクセス回線障害など'
        })
      }
      const timer = setTimeout(func, timeout)
      client
        .send(command)
        .then(result => {
          clearTimeout(timer)
          setLastAccessDate(Date.now())
          resolve(result)
        })
        .catch(error => {
          clearTimeout(timer)
          if (error.code === 'CredentialsError') {
            // STSトークンの期限切れ
            refreshToken()
              .then((_tokens: any) => {
                uploadDataProc(bucketParam, contentType, fileName, contents, timeout)
                  .then(contents => {
                    resolve(contents)
                  })
                  .catch(retryError => {
                    reject({ message: retryError })
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
              })
          } else {
            setLastAccessDate(Date.now())
            reject({ message: error })
          }
        })
    })
  }

  /**
   * deleteFileが呼び出す、S3 Bucket内のファイルの削除処理の本体。
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @returns If resolved then バージョン管理されていないBucketなら空のオブジェクト{}を返す。
   * バージョン管理されているBucketなら { DeleteMarker: (boolean)削除したオブジェクトにDeleteMarkerが付けられているかどうか,
   * VersionId: (String)削除によって作成されたDeleteMarkerのバージョン,
   * RequestCharged: (String)リクエストが正常に課金されているかどうか }
   * を返す。
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function deleteFileProc(
    bucketParam: BucketParameter,
    fileName: string
  ): Promise<DeleteObjectCommandOutput> {
    const client = new S3Client(s3ClientBaseConfig)
    const key = bucketParam.prefix + fileName
    const params = { Bucket: bucketParam.name, Key: key }
    const command = new DeleteObjectCommand(params)
    return await new Promise((resolve, reject) => {
      // S3 SDKやpromiseはキャンセル不可能でタイムアウトもしないので、独自に実装する。
      const func = (): void => {
        reject({
          message: 'API実行に失敗しました。\n原因: アクセス回線障害など'
        })
      }
      const timer = setTimeout(func, 5000)
      client
        .send(command)
        .then(result => {
          clearTimeout(timer)
          setLastAccessDate(Date.now())
          resolve(result)
        })
        .catch(error => {
          clearTimeout(timer)
          if (error.code === 'CredentialsError') {
            // STSトークンの期限切れ
            refreshToken()
              .then((_tokens: any) => {
                deleteFileProc(bucketParam, fileName)
                  .then(contents => {
                    resolve(contents)
                  })
                  .catch(retryError => {
                    reject({ message: retryError })
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
              })
          } else {
            setLastAccessDate(Date.now())
            reject({ message: error })
          }
        })
    })
  }
  /**
   * S3のオブジェクトに対して任意の期限を設定してアクセスさせるURLを生成する
   * @param bucketParam バケットを指定するパラメータ
   * @param key オブジェクト名
   * @param expires 有効期限(sec)
   */
  async function getSignedUrl(
    bucketParam: BucketParameter,
    key: string,
    expires: number = 1800
  ): Promise<string> {
    const client = new S3Client({ ...s3ClientBaseConfig, maxAttempts: 15 })
    const command = new GetObjectCommand({
      Bucket: bucketParam.name,
      Key: key
    })
    let result = ''
    try {
      result = await GetSignedURL(client, command, {
        expiresIn: expires
      })
      setLastAccessDate(Date.now())
    } catch (error) {
      console.error(error)
    }
    return result
  }
  /**
   * getFileListが呼び出す、ファイル一覧の取得処理を行う本体（閉域網用）。
   * ALB -> Container(Fargate/ECS) -> VPC Endpoint -> S3とたどってファイル一覧を取得する。
   * @param tokens modules/miiafw/MiiaFwCognito#getTokensによって取得されるセキュリティトークン情報
   * @param bucketParam バケットを指定するパラメータ
   * @returns If resolved then {commonPrefixes: [{Prefix: 'bucketParamで指定したprefix直下のフォルダ'}, ...], contents: [{Key: 'オブジェクトの名前', LastModified: 最終更新日時, ETag: "ETag", Size: ファイルサイズ(Byte), StorageClass: "ストレージクラス"}, {}, ...]}.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function getFileListProcClosed(
    tokens: TokenInfo,
    bucketParam: BucketParameter
  ): Promise<object> {
    const commonModalStore = useCommonModalStore()
    const { endpoint } = MiiaFwConfig.config.s3Access
    const { method } = MiiaFwConfig.config.authorization
    assert(
      typeof endpoint === 'string',
      'getFileListProcClosed を使うには MiiaFwConfig.config.s3Access.endpoint をセットしてください。'
    )
    assert(
      typeof method === 'string',
      'getFileListProcClosed を使うには MiiaFwConfig.config.authorization.method をセットしてください。'
    )

    return await new Promise((resolve, reject) => {
      // ALB -> Container(Fargate/ECS) -> VPC Endpoint -> S3
      // 【参考】ALB -> Lambda -> Endpoint -> S3方式は、ALBからLambdaへの送受信はBodyサイズに1MB制限があるので没!
      // ・<https://docs.amazonaws.cn/en_us/elasticloadbalancing/latest/application/load-balancer-troubleshooting.html#http-413-issues>
      // ・HTTP 413: Payload Too Large: The target is a Lambda function and the response body exceeds 1 MB
      const args = {
        function: 'list_objects',
        params: {
          bucket_name: bucketParam.name,
          prefix: bucketParam.prefix,
          delimiter: bucketParam.delimiter
        }
      }
      void webApiCall(endpoint, 'POST', { 'X-miiafw-token': tokens.session_id }, args, 'json')
        .then(data => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (
            Object.prototype.hasOwnProperty.call(data.body, 'result') &&
            Object.prototype.hasOwnProperty.call(data.body.result, 'isValid') &&
            data.body.result.isValid === false
          ) {
            // トークン有効期限切れ(reflesh & 1回リトライ)
            refreshToken()
              .then((_tokens: any) => {
                getFileList(bucketParam)
                  .then(success => {
                    resolve(success)
                  })
                  .catch(fault => {
                    reject(fault)
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
                void procAtSignout().then(_result => {
                  commonModalStore.openAlertModal({ body: refreshError })
                  if (useRouter().currentRoute.value.path !== '/') {
                    void useRouter().push({ path: '/' })
                  } // 同じページに遷移させるとNavigation Errorになる。
                })
              })
          } else {
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              reject({ message: data.body.error })
            } else {
              resolve(data.body.result)
            }
          }
        })
        .catch(error => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
        .then(() => {
          setLastAccessDate(Date.now())
        })
      /*
    // API Gateway -> Lambda -> S3
    // API Gatewayは、ペイロードサイズ: 10MB、セッションタイムアウト: 29[s]なのでこの方式は没!
    this.miiafwCognitolibs_invokeApiGwFunc(
      // API Gatewayのエンドポイント
      this.$apiGwEndpoint0,
      // Lambda関数をコールするためのWebAPIのサブディレクトリ
      '/miiafw-alb-backend',
      // Lambda関数に渡す引数
      {
        function: 'list_objects',
        params: { bucket_name: bucketParam.name, prefix: bucketParam.prefix, delimiter: bucketParam.delimiter }
      },
      // Lambda関数実行前の前処理のコールバック
      () => {},
      // 成功時のコールバック
      (data) => {
        resolve(JSON.parse(data.result))
      },
      // 例外受信時のコールバック(引数: エラーメッセージ)
      (message) => {
        reject({ message: message })
      },
      // 予期せぬエラー(アクセス回線障害など)発生時のコールバック
      () => {
        reject({ message: 'API実行に失敗しました。(原因: アクセス回線障害など)' })
      },
      // 処理完了後に実行するコールバック
      () => {}
    )
    */
    })
  }

  /**
   * downloadObjectが呼び出す、S3 Bucketからオブジェクトをダウンロードする処理の本体。
   * ALB -> Container(Fargate/ECS) -> VPC Endpoint -> S3と経路をたどる。
   * @param tokens modules/miiafw/MiiaFwCognito#getTokensによって取得されるセキュリティトークン情報
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @param objType オブジェクトダウンロードする時の型。binary, base64, blob, objectUrlが指定可能。
   * @param timeout タイムアウトまでの時間(msec)
   * @returns If resolved then オブジェクトデータ[Typed Array(Browser)].
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function downloadObjectProcClosed(
    tokens: TokenInfo,
    bucketParam: BucketParameter,
    fileName: string,
    objType: string,
    timeout: number = 60000
  ): Promise<any> {
    const { endpoint } = MiiaFwConfig.config.s3Access
    const { method } = MiiaFwConfig.config.authorization
    assert(
      typeof endpoint === 'string',
      'downloadObjectProcClosed を使うには MiiaFwConfig.config.s3Access.endpoint をセットしてください。'
    )
    assert(
      typeof method === 'string',
      'downloadObjectProcClosed を使うには MiiaFwConfig.config.authorization.method をセットしてください。'
    )

    return await new Promise((resolve, reject) => {
      // ALB -> Container(Fargate/ECS) -> VPC Endpoint -> S3
      // 【参考】ALB -> Lambda -> Endpoint -> S3方式は、ALBからLambdaへの送受信はBodyサイズに1MB制限があるので没!
      // ・<https://docs.amazonaws.cn/en_us/elasticloadbalancing/latest/application/load-balancer-troubleshooting.html#http-413-issues>
      // ・HTTP 413: Payload Too Large: The target is a Lambda function and the response body exceeds 1 MB

      const commonModalStore = useCommonModalStore()
      const args = {
        function: 'get_object',
        params: {
          bucket_name: bucketParam.name,
          key: bucketParam.prefix + fileName
        }
      }
      void webApiCall(
        endpoint,
        'POST',
        { 'X-miiafw-token': tokens.session_id },
        args,
        'json',
        timeout
      )
        .then(data => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (
            Object.prototype.hasOwnProperty.call(data.body, 'result') &&
            Object.prototype.hasOwnProperty.call(data.body.result, 'isValid') &&
            data.body.result.isValid === false
          ) {
            // トークン有効期限切れ(reflesh & 1回リトライ)
            refreshToken()
              .then((_tokens: any) => {
                downloadObject(bucketParam, fileName, objType)
                  .then(success => {
                    resolve(success)
                  })
                  .catch(fault => {
                    reject(fault)
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
                void procAtSignout().then(_result => {
                  commonModalStore.openAlertModal({ body: refreshError })
                  if (useRouter().currentRoute.value.path !== '/') {
                    void useRouter().push({ path: '/' })
                  } // 同じページに遷移させるとNavigation Errorになる。
                })
              })
          } else {
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              reject({ message: data.body.error })
            } else {
              // Base64をそのまま文字列型に変換したデータがdata.body.resultに格納されている。(lambda関数: miiafw-S3Manager)
              if (objType.toLowerCase() === 'base64') {
                resolve(data.body.result)
              }
              // window.atob()でBase64デコードして元データ(バイナリ)をそのまま文字列型に変換したデータを得る。
              const binaryString = window.atob(data.body.result)
              if (objType.toLowerCase() === 'binary') {
                // 文字列型にされたバイナリデータを元のバイナリ(ArrayBuffer)に戻す。
                const contents = stringToArrayBuffer(binaryString)
                resolve(contents)
              }
              // type属性を設定することもできるが、HTTPヘッダの「Content-Type」は「application/json」なので使用できない。
              const blob = stringToBlob(binaryString, fileName, data.body.contentType)
              if (objType.toLowerCase() === 'blob') {
                resolve(blob)
              }
              if (objType.toLowerCase() === 'objecturl') {
                resolve(window.URL.createObjectURL(blob))
              } else {
                reject({
                  message: 'ダウンロードするオブジェクトの型指定が不正です。'
                })
              }
            }
          }
        })
        .catch(error => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
        .then(() => {
          setLastAccessDate(Date.now())
        })
      /*
    // API Gateway -> Lambda -> S3
    // API Gatewayは、ペイロードサイズ: 10MB、セッションタイムアウト: 29[s]なのでこの方式は没!
    this.miiafwCognitolibs_invokeApiGwFunc(
      // API Gatewayのエンドポイント
      this.$apiGwEndpoint0,
      // Lambda関数をコールするためのWebAPIのサブディレクトリ
      '/miiafw-alb-backend',
      // Lambda関数に渡す引数
      {
        function: 'get_object',
        params: { bucket_name: bucketParam.name, key: bucketParam.prefix + fileName }
      },
      // Lambda関数実行前の前処理のコールバック
      () => {},
      // 成功時のコールバック
      (data) => {
        // Base64をそのまま文字列型に変換したデータがdata.resultに格納されている。(lambda関数: miiafw-S3Manager)
        // window.atob()でBase64デコードして元データ(バイナリ)をそのまま文字列型に変換したデータを得る。
        const binaryString = window.atob(data.result)
        // 文字列型にされたバイナリデータを元のバイナリ(ArrayBuffer)に戻す。
        const contents = this.miiafwlibs_stringToArrayBuffer(binaryString)
        // type属性を設定することもできるが、HTTPヘッダの「Content-Type」は「application/json」なので使用できない。
        const blob = new Blob([contents as any], { lastModifiedDate: new Date(), name: fileName })
        resolve(window.URL.createObjectURL(blob))
      },
      // 例外受信時のコールバック(引数: エラーメッセージ)
      (message) => {
        reject({ message: message })
      },
      // 予期せぬエラー(アクセス回線障害など)発生時のコールバック
      () => {
        reject({ message: 'API実行に失敗しました。(原因: アクセス回線障害など)' })
      },
      // 処理完了後に実行するコールバック
      () => {}
    )
    */
    })
  }

  /**
   * existsProcが呼び出す、Bucketにオブジェクトが存在するか確認する処理の本体（閉域網用）。
   * @param tokens modules/miiafw/MiiaFwCognito#getTokensによって取得されるセキュリティトークン情報
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @returns If resolved then True or False.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function existsProcClosed(
    tokens: TokenInfo,
    bucketParam: BucketParameter,
    fileName: string
  ): Promise<boolean> {
    const commonModalStore = useCommonModalStore()
    const { endpoint } = MiiaFwConfig.config.s3Access
    const { method } = MiiaFwConfig.config.authorization
    assert(
      typeof endpoint === 'string',
      'existsProcClosed を使うには MiiaFwConfig.config.s3Access.endpoint をセットしてください。'
    )
    assert(
      typeof method === 'string',
      'existsProcClosed を使うには MiiaFwConfig.config.authorization.method をセットしてください。'
    )

    return await new Promise((resolve, reject) => {
      // ALB -> Container(Fargate/ECS) -> VPC Endpoint -> S3
      // 【参考】ALB -> Lambda -> Endpoint -> S3方式は、ALBからLambdaへの送受信はBodyサイズに1MB制限があるので没!
      // ・<https://docs.amazonaws.cn/en_us/elasticloadbalancing/latest/application/load-balancer-troubleshooting.html#http-413-issues>
      // ・HTTP 413: Payload Too Large: The target is a Lambda function and the response body exceeds 1 MB
      const args = {
        function: 'head_object',
        params: {
          bucket_name: bucketParam.name,
          key: bucketParam.prefix + fileName
        }
      }
      void webApiCall(endpoint, 'POST', { 'X-miiafw-token': tokens.session_id }, args, 'json')
        .then(data => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (
            Object.prototype.hasOwnProperty.call(data.body, 'result') &&
            Object.prototype.hasOwnProperty.call(data.body.result, 'isValid') &&
            data.body.result.isValid === false
          ) {
            // トークン有効期限切れ(reflesh & 1回リトライ)
            refreshToken()
              .then(_tokens => {
                exists(bucketParam, fileName)
                  .then(success => {
                    resolve(success)
                  })
                  .catch(fault => {
                    reject(fault)
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
                void procAtSignout().then(_result => {
                  commonModalStore.openAlertModal({ body: refreshError })
                  if (useRouter().currentRoute.value.path !== '/') {
                    void useRouter().push({ path: '/' })
                  } // 同じページに遷移させるとNavigation Errorになる。
                })
              })
          } else {
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              const errorMsg: string = data.body.error.toLowerCase()
              if (errorMsg.includes('404') && errorMsg.includes('not found')) {
                resolve(false)
              } else {
                reject({ message: data.body.error })
              }
            } else {
              resolve(true)
            }
          }
        })
        .catch(error => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
        .then(() => {
          setLastAccessDate(Date.now())
        })
      /*
    // API Gateway -> Lambda -> S3
    // API Gatewayは、ペイロードサイズ: 10MB、セッションタイムアウト: 29[s]なのでこの方式は没!
    this.miiafwCognitolibs_invokeApiGwFunc(
      // API Gatewayのエンドポイント
      this.$apiGwEndpoint0,
      // Lambda関数をコールするためのWebAPIのサブディレクトリ
      '/miiafw-alb-backend',
      // Lambda関数に渡す引数
      {
        function: 'head_object',
        params: { bucket_name: bucketParam.name, key: bucketParam.prefix + fileName }
      },
      // Lambda関数実行前の前処理のコールバック
      () => {},
      // 成功時のコールバック
      (data) => {
        resolve(true)
      },
      // 例外受信時のコールバック(引数: エラーメッセージ)
      (message) => {
        const errorMsg = message.toLowerCase()
        if (errorMsg.contains('404') && errorMsg.contains('not found')) {
          resolve(false)
        } else {
          reject({ message: message })
        }
      },
      // 予期せぬエラー(アクセス回線障害など)発生時のコールバック
      () => {
        reject({ message: 'API実行に失敗しました。(原因: アクセス回線障害など)' })
      },
      // 処理完了後に実行するコールバック
      () => {}
    )
    */
    })
  }

  /**
   * uploadDataが呼び出す、S3 Bucketへデータをアップロードする処理の本体(閉域網用)。
   * @param tokens modules/miiafw/MiiaFwCognito#getTokensによって取得されるセキュリティトークン情報
   * @param bucketParam バケットを指定するパラメータ
   * @param contentType コンテンツのフォーマット（Content-Type）
   * @param fullPath ファイル名（S3上でのフルパス）
   * @param file オブジェクトデータ
   * @returns If resolved then リクエストに対するレスポンス(アップロードされたオブジェクトについての情報）.
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function uploadDataProcClosed(
    tokens: TokenInfo,
    bucketParam: BucketParameter,
    contentType: string,
    fullPath: string,
    file: File
  ): Promise<object> {
    const commonModalStore = useCommonModalStore()
    const { endpoint } = MiiaFwConfig.config.s3Access
    const { method } = MiiaFwConfig.config.authorization
    assert(
      typeof endpoint === 'string',
      'uploadDataProcClosed を使うには MiiaFwConfig.config.s3Access.endpoint をセットしてください。'
    )
    assert(
      typeof method === 'string',
      'uploadDataProcClosed を使うには MiiaFwConfig.config.authorization.method をセットしてください。'
    )

    return await new Promise((resolve, reject) => {
      // 「ALB -> Lambda -> Endpoint -> S3」と「API Gateway -> Lambda -> S3」共通
      if (file.size > MiiaFwConfig.config.s3Access.maxUploadSize) {
        reject({
          message: `アップロードできません。アップロード可能サイズ=${MiiaFwConfig.config.s3Access.maxUploadSize} bytes / ファイルサイズ=${file.size} bytes`
        })
      } else {
        const fr = new FileReader()
        fr.onload = evt => {
          assert(typeof evt.target?.result === 'string')
          const tmpList: string[] = (evt.target!.result! as string).split('base64,')
          if (tmpList.length > 0) {
            const base64Contests = tmpList[1]
            // ALB -> Container(Fargate/ECS) -> VPC Endpoint -> S3
            // 【参考】ALB -> Lambda -> Endpoint -> S3方式は、ALBからLambdaへの送受信はBodyサイズに1MB制限があるので没!
            // ・<https://docs.amazonaws.cn/en_us/elasticloadbalancing/latest/application/load-balancer-troubleshooting.html#http-413-issues>
            // ・HTTP 413: Payload Too Large: The target is a Lambda function and the response body exceeds 1 MB
            const args = {
              function: 'put_object',
              params: {
                bucket_name: bucketParam.name,
                key: bucketParam.prefix + fullPath,
                content_type: file.type,
                body: base64Contests
              }
            }
            void webApiCall(endpoint, 'POST', { 'X-miiafw-token': tokens.session_id }, args, 'json')
              .then(data => {
                if (typeof data === 'string') {
                  // IE対応
                  data = JSON.parse(data)
                }
                if (
                  Object.prototype.hasOwnProperty.call(data.body, 'result') &&
                  Object.prototype.hasOwnProperty.call(data.body.result, 'isValid') &&
                  data.body.result.isValid === false
                ) {
                  // トークン有効期限切れ(reflesh & 1回リトライ)
                  refreshToken()
                    .then(_tokens => {
                      uploadData(bucketParam, contentType, fullPath, file)
                        .then(success => {
                          resolve(success)
                        })
                        .catch(fault => {
                          reject(fault)
                        })
                    })
                    .catch(refreshError => {
                      reject({ message: refreshError })
                      void procAtSignout().then(_result => {
                        commonModalStore.openAlertModal({ body: refreshError })
                        if (useRouter().currentRoute.value.path !== '/') {
                          void useRouter().push({ path: '/' })
                        } // 同じページに遷移させるとNavigation Errorになる。
                      })
                    })
                } else {
                  if (Object.prototype.hasOwnProperty.call(data, 'error')) {
                    // ハンドリングされたエラー処理
                    reject({ message: data.body.error })
                  } else {
                    resolve(data.body.result)
                  }
                }
              })
              .catch(error => {
                if (typeof error === 'string') {
                  // IE対応
                  error = JSON.parse(error)
                }
                reject({ message: error.message })
              })
              .then(() => {
                setLastAccessDate(Date.now())
              })
            /*
          // API Gateway -> Lambda -> S3
          // API Gatewayは、ペイロードサイズ: 10MB、セッションタイムアウト: 29[s]なのでこの方式は没!
          this.miiafwCognitolibs_invokeApiGwFunc(
            // API Gatewayのエンドポイント
            this.$apiGwEndpoint0,
            // Lambda関数をコールするためのWebAPIのサブディレクトリ
            '/miiafw-alb-backend',
            // Lambda関数に渡す引数
            {
              function: 'put_object',
              params: { bucket_name: bucketParam.name, key: bucketParam.prefix + file.name, content_type: file.type, body: base64Contests }
            },
            // Lambda関数実行前の前処理のコールバック
            () => {},
            // 成功時のコールバック
            (data) => {
              resolve(data.result)
            },
            // 例外受信時のコールバック(引数: エラーメッセージ)
            (message) => {
              reject({ message: message })
            },
            // 予期せぬエラー(アクセス回線障害など)発生時のコールバック
            () => {
              reject({ message: 'API実行に失敗しました。(原因: アクセス回線障害など)' })
            },
            // 処理完了後に実行するコールバック
            () => {}
          )
          */
          } else {
            reject({ message: 'Base64変換できませんでした。' })
          }
        }
        fr.readAsDataURL(file)
      }
    })
  }

  /**
   * deleteFileが呼び出す、S3 Bucket内のファイルの削除処理の本体（閉域網用）。
   * ALB -> Container(Fargate/ECS) -> VPC Endpoint -> S3と経路をたどる。
   * @param tokens modules/miiafw/MiiaFwCognito#getTokensによって取得されるセキュリティトークン情報
   * @param bucketParam バケットを指定するパラメータ
   * @param fileName ファイル名
   * @returns バージョン管理されていないBucketなら空のオブジェクト{}を返す。
   * バージョン管理されているBucketなら { DeleteMarker: (boolean)削除したオブジェクトにDeleteMarkerが付けられているかどうか,
   * VersionId: (String)削除によって作成されたDeleteMarkerのバージョン,
   * RequestCharged: (String)リクエストが正常に課金されているかどうか }
   * を返す。
   * If rejected then {message: '[エラーメッセージ]'}.
   */
  async function deleteFileProcClosed(
    tokens: TokenInfo,
    bucketParam: BucketParameter,
    fileName: string
  ): Promise<any> {
    const commonModalStore = useCommonModalStore()
    const { endpoint } = MiiaFwConfig.config.s3Access
    const { method } = MiiaFwConfig.config.authorization
    assert(
      typeof endpoint === 'string',
      'deleteFileProcClosed を使うには MiiaFwConfig.config.s3Access.endpoint をセットしてください。'
    )
    assert(
      typeof method === 'string',
      'deleteFileProcClosed を使うには MiiaFwConfig.config.authorization.method をセットしてください。'
    )

    return await new Promise((resolve, reject) => {
      // ALB -> Container(Fargate/ECS) -> VPC Endpoint -> S3
      // 【参考】ALB -> Lambda -> Endpoint -> S3方式は、ALBからLambdaへの送受信はBodyサイズに1MB制限があるので没!
      // ・<https://docs.amazonaws.cn/en_us/elasticloadbalancing/latest/application/load-balancer-troubleshooting.html#http-413-issues>
      // ・HTTP 413: Payload Too Large: The target is a Lambda function and the response body exceeds 1 MB
      const args = {
        function: 'delete_object',
        params: {
          bucket_name: bucketParam.name,
          key: bucketParam.prefix + fileName
        }
      }
      void webApiCall(endpoint, 'POST', { 'X-miiafw-token': tokens.session_id }, args, 'json')
        .then(data => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (
            Object.prototype.hasOwnProperty.call(data.body, 'result') &&
            Object.prototype.hasOwnProperty.call(data.body.result, 'isValid') &&
            data.body.result.isValid === false
          ) {
            // トークン有効期限切れ(reflesh & 1回リトライ)
            refreshToken()
              .then(_tokens => {
                deleteFile(bucketParam, fileName)
                  .then(success => {
                    resolve(success)
                  })
                  .catch(fault => {
                    reject(fault)
                  })
              })
              .catch(refreshError => {
                reject({ message: refreshError })
                void procAtSignout().then(_result => {
                  commonModalStore.openAlertModal({ body: refreshError })
                  if (useRouter().currentRoute.value.path !== '/') {
                    void useRouter().push({ path: '/' })
                  } // 同じページに遷移させるとNavigation Errorになる。
                })
              })
          } else {
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              reject({ message: data.body.error })
            } else {
              resolve(true)
            }
          }
        })
        .catch(error => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
        .then(() => {
          setLastAccessDate(Date.now())
        })
      /*
    // API Gateway -> Lambda -> S3
    // API Gatewayは、ペイロードサイズ: 10MB、セッションタイムアウト: 29[s]なのでこの方式は没!
    this.miiafwCognitolibs_invokeApiGwFunc(
      // API Gatewayのエンドポイント
      this.$apiGwEndpoint0,
      // Lambda関数をコールするためのWebAPIのサブディレクトリ
      '/miiafw-alb-backend',
      // Lambda関数に渡す引数
      {
        function: 'delete_object',
        params: { bucket_name: bucketParam.name, key: bucketParam.prefix + fileName }
      },
      // Lambda関数実行前の前処理のコールバック
      () => {},
      // 成功時のコールバック
      (data) => {
        resolve(true)
      },
      // 例外受信時のコールバック(引数: エラーメッセージ)
      (message) => {
        reject({ message: message })
      },
      // 予期せぬエラー(アクセス回線障害など)発生時のコールバック
      () => {
        reject({ message: 'API実行に失敗しました。(原因: アクセス回線障害など)' })
      },
      // 処理完了後に実行するコールバック
      () => {}
    )
    */
    })
  }

  return { downloadObject, uploadData, deleteFile, getFileList }
}
